/**
 * License Domain Logic
 *
 * Pure functions for license lifecycle: status resolution, expiration, sorting, and filtering.
 * No external dependencies (Firebase, Vue, moment).
 */
import { computeLicenseExpirationDate, isLicenseExpired } from './licenseExpiration.js';
import { toDate } from "./dateUtils.js";

export const LicenseStatusType = Object.freeze({
  active: 'active',
  pending: 'pending',
  inactive: 'inactive',
  expired: 'expired',
  none: 'none'
});

export const UNSPECIFIED_LICENSE_TYPE = '__unspecified__';

/**
 * Built-in status definitions (code/label/type).
 */
export const BUILTIN_LICENSE_STATUSES = [
  ['submitted', 'Unpaid', LicenseStatusType.pending],
  ['issued', 'Active', LicenseStatusType.active],
  ['expired', 'Expired', LicenseStatusType.inactive],
  ['suspended', 'Suspended', LicenseStatusType.pending],
  ['closed', 'Closed', LicenseStatusType.inactive],
  ['withdrawn', 'Withdrawn', LicenseStatusType.inactive],
  ['deleted', 'Deleted', LicenseStatusType.inactive]
].map(status => {
  return {
    id: status[0],
    code: status[0],
    label: status[1],
    type: status[2] || LicenseStatusType.pending,
    builtIn: true
  };
});

/**
 * Compare two status objects for sorting.
 * Orders by type priority (pending > active > inactive), then by builtIn, then by label.
 */
export function compareStatuses(s1, s2) {
  const order = {
    [LicenseStatusType.pending]: 1,
    [LicenseStatusType.active]: 2,
    [LicenseStatusType.inactive]: 3,
    [LicenseStatusType.expired]: 3,
    [LicenseStatusType.none]: 4,
    undefined: 5
  };
  let d = (order[s1?.type] || 0) - (order[s2?.type] || 0);
  if (d === 0) {
    d = (s1?.builtIn ? 1 : 0) - (s2?.builtIn ? 1 : 0);
  }
  if (d === 0) {
    d = (s1?.label || '').localeCompare(s2?.label || '');
  }
  return d;
}

/**
 * Find a status in the config by code.
 */
function findStatus(statusConfig = [], code) {
  return statusConfig.find(status => status.code === code);
}

/**
 * Derive the status type from workflow status and expiration flag.
 *
 * @param {Object} params
 * @param {string|null|undefined} params.workflowStatus - The license's workflow status code
 * @param {boolean} params.isExpired - Whether the license is expired
 * @param {Array<{code: string, type: string}>} params.statusConfig - City's configured status types
 * @returns {string} One of: 'none', 'expired', 'inactive', 'pending', 'active'
 */
export function computeLicenseStatusType({
  workflowStatus,
  isExpired,
  statusConfig = []
}) {
  if (isExpired) {
    return LicenseStatusType.expired;
  }

  if (!workflowStatus) {
    return LicenseStatusType.none;
  }

  const configuredStatus = findStatus(statusConfig, workflowStatus);
  const statusType = configuredStatus?.type;

  if (statusType === LicenseStatusType.active) {
    return LicenseStatusType.active;
  } else if (statusType === LicenseStatusType.pending) {
    return LicenseStatusType.pending;
  } else if (statusType === LicenseStatusType.inactive) {
    return LicenseStatusType.inactive;
  } else {
    return LicenseStatusType.none;
  }
}

/**
 * Return the status object (from config) and computed status type.
 */
export function resolveLicenseStatus({
  workflowStatus,
  isExpired,
  statusConfig = []
}) {
  const effectiveCode = isExpired ? 'expired' : workflowStatus;
  const status = findStatus(statusConfig, effectiveCode);
  const type = computeLicenseStatusType({ workflowStatus, isExpired, statusConfig });
  return { status, type, code: effectiveCode };
}

/**
 * Return effective workflow status code with expired override.
 */
export function computeWorkflowStatus({ workflowStatus, isExpired }) {
  return isExpired ? 'expired' : workflowStatus;
}

/**
 * Check if a license status type represents an "active" license
 * (one that would prevent a property from being in violation).
 *
 * @param {string} licenseStatusType - One of the LicenseStatusType values
 * @returns {boolean} True if the license is considered active (active or pending)
 */
export function isLicenseTypeActive(licenseStatusType) {
  return licenseStatusType === LicenseStatusType.active ||
    licenseStatusType === LicenseStatusType.pending;
}

// Alias for backward compatibility with violation.js
export const isLicenseActive = isLicenseTypeActive;

/**
 * Domain-level "active" (used by License model): issued/closed OR active-type, and not expired.
 */
export function isLicenseLifecycleActive({
  workflowStatus,
  isExpired,
  statusConfig = []
}) {
  const { type } = resolveLicenseStatus({ workflowStatus, isExpired, statusConfig });
  if (isExpired) return false;
  return workflowStatus === 'issued' ||
    workflowStatus === 'closed' ||
    type === LicenseStatusType.active;
}

export function isLicenseLifecyclePending({
  workflowStatus,
  isExpired,
  statusConfig = []
}) {
  if (isExpired) return false;
  const { type } = resolveLicenseStatus({ workflowStatus, isExpired, statusConfig });
  return type === LicenseStatusType.pending;
}

export function isLicenseLifecycleInactive({
  workflowStatus,
  isExpired,
  statusConfig = []
}) {
  if (isExpired) return true;
  const { type } = resolveLicenseStatus({ workflowStatus, isExpired, statusConfig });
  return type === LicenseStatusType.inactive || type === LicenseStatusType.expired;
}

/**
 * Compute expiration date and flag.
 *
 * @param {Object} params
 * @param {Date|string|number|null} params.dateIssued - The date the license was issued
 * @param {string} params.expirationType - 'one-year', 'annual-date', or 'end-of-year'
 * @param {string} [params.periodStartMonthDay='01-01'] - Start of the next annual period
 * @param {number} [params.earlyIssueMonthAllowance=1] - Months before year-end that roll over
 * @param {Date} [params.now=new Date()] - Current date for comparison
 * @returns {{ expirationDate: Date|null, isExpired: boolean }}
 */
export function computeExpiration({ dateIssued, expirationType, periodStartMonthDay, earlyIssueMonthAllowance = 1, now = new Date() }) {
  const expirationDate = computeLicenseExpirationDate({
    dateIssued,
    expirationType,
    periodStartMonthDay,
    earlyIssueMonthAllowance
  });
  const isExpired = isLicenseExpired(expirationDate, now);
  return { expirationDate, isExpired };
}

/**
 * Filter helper for licenses using provided field accessors.
 */
export function licensePassesFilter({
  status,
  licenseType,
  hasProperty,
  getCustomFieldValue,
  statusAllowlist = {},
  licenseTypeAllowlist = {},
  onlyShowMissingProperties = false,
  customFieldOptions = {}
}) {
  if (status && Object.keys(statusAllowlist).length > 0) {
    if (!statusAllowlist[status]) return false;
  }
  if (onlyShowMissingProperties && !!hasProperty) {
    return false;
  }
  if (Object.keys(licenseTypeAllowlist).length > 0) {
    const filterValue = licenseType && Object.prototype.hasOwnProperty.call(licenseTypeAllowlist, licenseType)
      ? licenseType
      : UNSPECIFIED_LICENSE_TYPE;
    if (!licenseTypeAllowlist[filterValue]) return false;
  }
  for (let fieldId in customFieldOptions) {
    const allowed = customFieldOptions[fieldId];
    const fieldValue = getCustomFieldValue ? getCustomFieldValue(fieldId) : null;
    if (!allowed[fieldValue]) {
      return false;
    }
  }
  return true;
}

/**
 * Compare two licenses by address (street name, then street number).
 */
export function compareLicensesByAddress(l1, l2) {
  const a1 = l1?.property?.address;
  const a2 = l2?.property?.address;
  if (a1 && a2) {
    let diff = (a1.street || '').localeCompare(a2.street || '');
    if (diff === 0) {
      return (a1.streetNumber || 0) - (a2.streetNumber || 0);
    } else {
      return diff;
    }
  } else if (!a1) {
    return 1;
  } else {
    return -1;
  }
}

/**
 * Sort licenses by status (then other fields) using domain compare.
 */
export function compareLicensesByStatus(l1, l2) {
  const compareDates = (d1, d2) => {
    if (d1 && d2) return d2 - d1;
    else if (d1) return -1;
    else if (d2) return 1;
    else return 0;
  };

  let diff = compareStatuses(l1?.statusWithConfig, l2?.statusWithConfig);
  if (diff !== 0) return diff;

  diff = compareDates(l1?.dateIssued, l2?.dateIssued);
  if (diff !== 0) return diff;

  diff = compareDates(l1?.dateApplied, l2?.dateApplied);
  if (diff !== 0) return diff;

  return compareLicensesByAddress(l1, l2);
}

/**
 * Compare two date values for sorting. Nulls sort to the end.
 */
function compareDateValues(d1, d2) {
  d1 = toDate(d1);
  d2 = toDate(d2);
  if (d1 && d2) return d2 - d1;
  if (d1) return -1;
  if (d2) return 1;
  return 0;
}

export function compareLicensesByDateIssued(l1, l2) {
  return compareDateValues(l1?.dateIssued, l2?.dateIssued) || compareLicensesByAddress(l1, l2);
}

export function compareLicensesByDateApplied(l1, l2) {
  return compareDateValues(l1?.appliedOn, l2?.appliedOn) || compareLicensesByAddress(l1, l2);
}

export function compareLicensesByDateExpires(l1, l2) {
  return compareDateValues(l1?.expirationDate, l2?.expirationDate) || compareLicensesByAddress(l1, l2);
}
